import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { resolveMutationErrorMessage } from "@/shared/api/error-message";
import { useDeleteUser } from "./use-delete-user";
import { resolveSelfGuard, shouldBlockSelfAction } from "./self-guard";
import type { UserDetail } from "./types";

interface DeleteUserDialogProps {
  readonly user: UserDetail | null;
  /** Firebase UID of the signed-in admin — see `ChangeRoleDialog` / `DeactivateUserDialog`. */
  readonly currentAdminFirebaseUid: string | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onDeleted: () => void;
}

/**
 * Non-empty list of reasons the delete is blocked. Mirrors the gates
 * the backend enforces in API-1.16 so the operator sees the same list
 * client-side. Order mirrors the doc.
 */
type BlockReason = "self" | "unverified" | "signedIn" | "ownedProperties" | "ownedVenues";

function collectBlockReasons(
  user: UserDetail,
  currentAdminFirebaseUid: string | null,
): readonly BlockReason[] {
  const reasons: BlockReason[] = [];
  const guard = resolveSelfGuard(currentAdminFirebaseUid, user.firebaseUid);
  if (guard === "self") reasons.push("self");
  if (guard === "unverified") reasons.push("unverified");
  if (user.lastSignInAt !== null) reasons.push("signedIn");
  if (user.ownedProperties.length > 0) reasons.push("ownedProperties");
  if (user.ownedVenues.length > 0) reasons.push("ownedVenues");
  return reasons;
}

export function DeleteUserDialog({
  user,
  currentAdminFirebaseUid,
  open,
  onOpenChange,
  onDeleted,
}: DeleteUserDialogProps) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const mutation = useDeleteUser();

  useEffect(() => {
    if (open) setError(null);
  }, [open, user?.id]);

  if (!user) return null;

  const reasons = collectBlockReasons(user, currentAdminFirebaseUid);
  const guard = resolveSelfGuard(currentAdminFirebaseUid, user.firebaseUid);
  // Even if reasons is empty, still block when the self-guard is not
  // strictly 'other'. Belt-and-suspenders — the list is the human copy,
  // the boolean is the gate.
  const isBlocked = reasons.length > 0 || shouldBlockSelfAction(guard);

  const handleOpenChange = (next: boolean) => {
    if (!next && mutation.isPending) return;
    onOpenChange(next);
  };

  const handleConfirm = () => {
    if (isBlocked) return;
    setError(null);
    mutation.mutate(user.id, {
      onSuccess: () => {
        onOpenChange(false);
        onDeleted();
      },
      onError: (err) => {
        setError(
          resolveMutationErrorMessage(err, t, {
            forbiddenKey: "users.errors.forbidden",
            serverPrefixKey: "users.errors.serverPrefix",
            genericKey: "users.delete.error",
          }),
        );
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="delete-user-dialog">
        <DialogHeader>
          <DialogTitle>{t("users.delete.title")}</DialogTitle>
          <DialogDescription>
            {t("users.delete.description", {
              name: user.displayName || user.email,
            })}
          </DialogDescription>
        </DialogHeader>

        {reasons.length > 0 && (
          <div
            data-testid="delete-user-blocked"
            className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3"
          >
            <AlertTriangle
              className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600"
              aria-hidden="true"
            />
            <div className="space-y-2">
              <p className="font-sans text-xs font-medium text-amber-900 dark:text-amber-200">
                {t("users.delete.blockedTitle")}
              </p>
              <ul className="list-inside list-disc space-y-0.5 font-sans text-xs font-normal text-amber-900 dark:text-amber-200">
                {reasons.map((r) => (
                  <li key={r} data-testid={`delete-user-reason-${r}`}>
                    {t(`users.delete.reason.${r}`)}
                  </li>
                ))}
              </ul>
              <p className="font-sans text-xs font-normal text-amber-900 dark:text-amber-200">
                {t("users.delete.blockedHint")}
              </p>
            </div>
          </div>
        )}

        {reasons.length === 0 && (
          <p className="font-sans text-sm font-medium text-destructive">
            {t("users.delete.irreversibleWarning")}
          </p>
        )}

        {error && (
          <p
            data-testid="delete-user-error"
            className="font-sans text-sm font-medium text-destructive"
          >
            {error}
          </p>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t("users.actions.cancel")}
          </Button>
          <Button
            data-testid="confirm-delete-user-button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={isBlocked || mutation.isPending}
          >
            {mutation.isPending ? t("users.delete.pending") : t("users.delete.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
